/**
 * Picking one record by typing part of its name — a deal's person, an
 * activity's organization, a user or a group. The one autocomplete every
 * picker is built on: `SubjectPicker` is this with the subject's avatar,
 * caption and default labels filled in.
 *
 * **Controlled and fetch-free**: the options and the loading flag come in as
 * props, and typing raises `onQueryChange`. The page does the searching,
 * through `hooks/useRecordSearch`, so this knows no endpoint and no entity —
 * only how to name one (`getLabel`), tell two apart (`getKey`) and,
 * optionally, add a second line (`getCaption`) and an icon (`getIcon`). A
 * component that searched for itself could not serve two screens searching
 * the same directory for different purposes, nor render in a test without a
 * server.
 *
 * The current value is always offered even when the search did not return it,
 * so an edit form opens showing what is already chosen.
 */
import type { Key, ReactNode } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';

export interface RecordPickerProps<T> {
  /** What the search returned for the current query. */
  options: readonly T[];
  value: T | null;
  getKey: (record: T) => Key;
  getLabel: (record: T) => string;
  /** A second, smaller line under the label. */
  getCaption?: (record: T) => string | null | undefined;
  /** An icon or avatar before the label in the list. */
  getIcon?: (record: T) => ReactNode;
  loading?: boolean;
  label: string;
  placeholder?: string;
  helperText?: string;
  /** Shown when the search returned nothing. Translated by the caller. */
  noOptionsText: string;
  disabled?: boolean;
  autoFocus?: boolean;
  onChange: (record: T | null) => void;
  /** Raises on every keystroke. Debounce it in the page — `useRecordSearch` does. */
  onQueryChange: (query: string) => void;
}

export function RecordPicker<T>({
  options,
  value,
  getKey,
  getLabel,
  getCaption,
  getIcon,
  loading = false,
  label,
  placeholder,
  helperText,
  noOptionsText,
  disabled = false,
  autoFocus = false,
  onChange,
  onQueryChange,
}: RecordPickerProps<T>) {
  const offered =
    value !== null && !options.some((option) => getKey(option) === getKey(value)) ? [value, ...options] : [...options];

  return (
    <Autocomplete<T>
      options={offered}
      value={value}
      disabled={disabled}
      loading={loading}
      // The server already matched; filtering again here would hide results
      // whose match was on a field this component never sees.
      filterOptions={(all) => all}
      isOptionEqualToValue={(option, selected) => getKey(option) === getKey(selected)}
      getOptionLabel={getLabel}
      onChange={(_event, selected) => onChange(selected)}
      onInputChange={(_event, input, reason) => {
        // Choosing an option writes its label into the input. That is not
        // something the user typed, and searching for it would replace the
        // options with one result the moment one is picked.
        if (reason === 'input' || reason === 'clear') onQueryChange(input);
      }}
      noOptionsText={noOptionsText}
      renderOption={(props, option) => {
        const { key: _key, ...liProps } = props;
        const caption = getCaption?.(option);
        return (
          <Box component="li" key={getKey(option)} {...liProps} sx={{ gap: 1.5 }}>
            {getIcon?.(option)}
            <ListItemText
              primary={getLabel(option)}
              secondary={caption || undefined}
              slotProps={{ secondary: { variant: 'caption' } }}
            />
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          {...(placeholder ? { placeholder } : {})}
          {...(helperText ? { helperText } : {})}
          autoFocus={autoFocus}
          slotProps={{
            // Spread params.slotProps first — see this folder's README, "The MUI
            // gotcha these autocompletes hit".
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              endAdornment: (
                <>
                  {loading && <CircularProgress size={16} />}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}
